import React, { useEffect, useState } from "react";
import { BrandMark, Icon, IconButton, type IconName } from "@nelurio/ui";
import { GlobalSearchOverlay } from "../components/GlobalSearchOverlay";
import { useI18n, languages } from "@nelurio/i18n";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { api, isDemoMode, API_BASE_URL } from "../api/client";
import { useFocusTimer } from "../focus/FocusTimerProvider";
import { OnboardingTour, TOUR_STORAGE_KEY } from "../components/OnboardingTour";
import { NotificationBell } from "../components/NotificationBell";
import { DayClosePopup } from "../components/DayClosePopup";
import { useNotificationPermissionPrompt } from "../lib/localNotifications";
import { subscribeKhoze, type KhozeJob } from "../lib/khozeEngine";
import { KhozeAvatar } from "../components/Khoze";

/* Primary tabs stay in the sidebar; everything else lives in "More" so the
   sidebar never scrolls. */
const mainItems: Array<{ to: string; key: string; icon: IconName }> = [
  { to: "/", key: "nav.dashboard", icon: "home" },
  { to: "/courses", key: "nav.courses", icon: "book" },
  { to: "/library", key: "nav.library", icon: "library" },
  { to: "/assistant", key: "nav.assistant", icon: "sparkle" },
  { to: "/tasks", key: "nav.tasks", icon: "check" },
  { to: "/more", key: "nav.more", icon: "more" }
];

const mobileItems = [
  { to: "/", key: "nav.dashboard", icon: "home" as IconName },
  { to: "/courses", key: "nav.courses", icon: "book" as IconName },
  { to: "/assistant", key: "nav.assistant", icon: "sparkle" as IconName },
  { to: "/tasks", key: "nav.tasks", icon: "check" as IconName },
  { to: "/more", key: "nav.more", icon: "more" as IconName }
];

// Firewall-deterministic back rule: a tab root is its own final destination —
// no back button there. Every deeper page belongs to exactly one tab root and
// back always returns to THAT root (never fuzzy history-walking).
const TAB_ROOTS = ["/", "/courses", "/library", "/assistant", "/tasks", "/more", "/focus", "/planner", "/progress", "/academic-path", "/settings"];

export function AppShell() {
  const { t, locale, setLocale, formatNumber } = useI18n();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [khozeJob, setKhozeJob] = useState<KhozeJob | null>(null);
  useEffect(() => subscribeKhoze(setKhozeJob), []);
  // Connection restored (wifi back, VPN toggled): nudge every surface to refetch.
  useEffect(() => {
    const back = () => window.dispatchEvent(new Event("nelurio:data-changed"));
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, []);
  const focus = useFocusTimer();
  const [searchOpen, setSearchOpen] = useState(false);
  // Header avatar: the uploaded profile photo replaces the initial letter.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  // Global search overlay (Ctrl/Cmd+K or the header button).
  const [storageBytes, setStorageBytes] = useState<number | null>(null);
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setSearchOpen(false);
  }, [location.pathname]);

  // Header avatar mirrors the profile upload (Settings dispatches the event).
  useEffect(() => {
    if (!user) { setAvatarUrl(null); return; }
    const load = () => {
      fetch(`${API_BASE_URL}/profile/avatar`, { credentials: "include" })
        .then((response) => setAvatarUrl(response.ok ? response.url : null))
        .catch(() => setAvatarUrl(null));
    };
    load();
    window.addEventListener("nelurio:avatar-changed", load);
    return () => window.removeEventListener("nelurio:avatar-changed", load);
  }, [user]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen((value) => !value);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (isDemoMode()) return;
    api<{ resources: { byteTotal: number } }>("/progress")
      .then((stats) => setStorageBytes(Number(stats?.resources?.byteTotal ?? 0)))
      .catch(() => setStorageBytes(null));
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    const replay = window.sessionStorage.getItem("nelurio.tour.replay") === "1";
    if (replay) {
      window.sessionStorage.removeItem("nelurio.tour.replay");
      setTourOpen(true);
      return;
    }
    const noTour = new URLSearchParams(window.location.search).has("notour");
    if (!noTour && !window.localStorage.getItem(TOUR_STORAGE_KEY)) {
      const timer = window.setTimeout(() => setTourOpen(true), 900);
      return () => window.clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    const handler = () => setTourOpen(true);
    window.addEventListener("nelurio:replay-tour", handler);
    return () => window.removeEventListener("nelurio:replay-tour", handler);
  }, []);

  // Opt-in device notifications (time-blocked tasks, routines, review digest).
  useNotificationPermissionPrompt(Boolean(user));

  // Push resync on EVERY entry: granted users must stay subscribed even when
  // the app is closed — endpoints expire, new devices appear, and users who
  // granted permission before the push wiring existed get subscribed now.
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    import("../lib/webPush").then(({ enableWebPush }) => void enableWebPush()).catch(() => undefined);
    const onVisible = () => { if (document.visibilityState === "visible") { import("../lib/webPush").then(({ enableWebPush }) => void enableWebPush()).catch(() => undefined); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user]);

  // Cross-device sync: when this tab regains focus or the poll fires, tell
  // every live data hook (courses, dashboard, notifications) to refetch —
  // changes made on the phone appear on the desktop within seconds.
  useEffect(() => {
    if (!user) return;
    const bump = () => window.dispatchEvent(new Event("nelurio:data-changed"));
    const onVisible = () => { if (document.visibilityState === "visible") bump(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") bump(); }, 45_000);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [user]);

  const quotaBytes = 2 * 1024 * 1024 * 1024;
  const usedLabel = storageBytes === null
    ? "…"
    : storageBytes >= 1024 * 1024 * 1024
      ? `${formatNumber(Number((storageBytes / (1024 * 1024 * 1024)).toFixed(1)))} GB`
      : `${formatNumber(Math.max(1, Math.round(storageBytes / (1024 * 1024))))} MB`;
  const storagePercent = Math.min(100, Math.round(((storageBytes ?? 0) / quotaBytes) * 100));

  return (
    <div className="app-viewport">
      <aside className="app-sidebar">
        <Link to="/" className="app-sidebar__brand"><BrandMark /></Link>
        <nav className="app-sidebar__nav" aria-label="Primary navigation">
          {mainItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"} className={({ isActive }) => `sidebar-link ${isActive ? "is-active" : ""}`}>
              <span className="sidebar-link__icon"><Icon name={item.icon} /></span>
              <span>{t(item.key)}</span>
              <i />
            </NavLink>
          ))}
        </nav>
        <div className="app-sidebar__bottom">
          <Link className="sidebar-link" to="/settings"><span className="sidebar-link__icon"><Icon name="settings" /></span><span>{t("nav.settings")}</span></Link>
          <button className="sidebar-link" onClick={() => void logout()}><span className="sidebar-link__icon"><Icon name="logout" /></span><span>{t("auth.signOut")}</span></button>
          <div className="storage-meter">
            <div><span>{t("admin.storage")}</span><strong>{usedLabel} / {formatNumber(2)} GB</strong></div>
            <span><i style={{ width: `${storagePercent}%` }} /></span>
          </div>
        </div>
      </aside>

      <div className="app-stage">
        <header className="topbar">
          {(() => {
            const firstSegment = "/" + (location.pathname.split("/").filter(Boolean)[0] ?? "");
            const isTabRoot = TAB_ROOTS.includes(firstSegment) && location.pathname === firstSegment;
            if (isTabRoot) return null;
            const backTarget = firstSegment !== "/" && TAB_ROOTS.includes(firstSegment) ? firstSegment : "/";
            return (
              <button className="back-button" onClick={() => navigate(backTarget)} aria-label={t("common.back")}>
                <Icon name="arrow" size={18} className="back-button__icon" />
              </button>
            );
          })()}
          <button className="topbar__mobile-brand" aria-label="Nelurio"><BrandMark compact /></button>
          <button className={`global-search ${searchOpen ? "is-open" : ""}`} onClick={() => setSearchOpen(true)}>
            <Icon name="search" size={18} />
            <span>{t("common.search")}</span>
            <kbd>{/Mac|iPhone|iPad/.test(navigator.platform) ? "⌘ K" : "Ctrl K"}</kbd>
          </button>
          <GlobalSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
          <div className="topbar__actions">
            <select className="language-mini" value={locale} onChange={(event) => setLocale(event.target.value as never)} aria-label={t("settings.language")}>
              {languages.map((language) => <option key={language.code} value={language.code}>{language.nativeLabel}</option>)}
            </select>
            <NotificationBell />
            <Link to="/settings" className="user-chip">
              <span>{avatarUrl ? <img src={avatarUrl} alt="" /> : user?.displayName?.slice(0, 1).toLocaleUpperCase(locale) || "N"}</span>
              <div><strong>{user?.displayName || "Nelurio"}</strong><small>{user?.plan || "free"}</small></div>
            </Link>
          </div>
        </header>

        <main className="app-main">
          <div key={location.pathname} className="route-view">
            <Outlet />
          </div>
        </main>
      </div>

      <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
        {mobileItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === "/"} className={({ isActive }) => {
            const moreSubPages = ["/library", "/focus", "/progress", "/academic-path", "/settings", "/planner"];
            const isMoreSection = item.to === "/more" && moreSubPages.includes(location.pathname);
            return `mobile-nav-link ${isActive || isMoreSection ? "is-active" : ""}`;
          }}>
            <span><Icon name={item.icon} size={19} /></span>
            <small>{t(item.key)}</small>
          </NavLink>
        ))}
      </nav>

      {focus.running && location.pathname !== "/focus" && (
        <div className={`focus-float ${focus.paused ? "is-paused" : ""}`}>
          <Link to="/focus" className="focus-float__main">
            <span className="focus-float__pulse"><Icon name="timer" size={16} /></span>
            <strong>{String(Math.floor(focus.secondsLeft / 60)).padStart(2, "0")}:{String(focus.secondsLeft % 60).padStart(2, "0")}</strong>
            <small>{focus.goal || t("focus.title")}</small>
          </Link>
          <button onClick={() => (focus.paused ? focus.resume() : focus.pause())} aria-label={focus.paused ? t("focus.resume") : t("focus.pause")}>
            <Icon name={focus.paused ? "play" : "pause"} size={15} />
          </button>
          <button onClick={() => void focus.stopAndSave()} aria-label={t("focus.finish")}>
            <Icon name="check" size={15} />
          </button>
        </div>
      )}

      {khozeJob && location.pathname !== "/assistant" && (
        <div className={`khoze-float ${khozeJob.status === "done" ? "is-ready" : ""}`}>
          <button onClick={() => navigate("/assistant")} aria-label={khozeJob.status === "done" ? t("assistant.replyReady") : t("assistant.thinkingFloat")}>
            <span className="khoze-float__avatar" aria-hidden="true"><KhozeAvatar size={30} speaking={khozeJob.status === "thinking"} /></span>
            <span className="khoze-float__copy">
              <strong>{khozeJob.status === "done" ? t("assistant.replyReady") : t("assistant.thinkingFloat")}</strong>
              <small>{khozeJob.question}</small>
            </span>
            {khozeJob.status === "thinking" && <span className="khoze-float__dots" aria-hidden="true"><i /><i /><i /></span>}
            <Icon name={khozeJob.status === "done" ? "check" : "message"} size={16} />
          </button>
        </div>
      )}

      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />
      <DayClosePopup />
    </div>
  );
}
